import classNames from 'classnames';
import { LinkFactoryReactRouter } from 'common/link/link-react-router.js';
import { Mountain } from 'components/avalanche-advisory/mountain.js';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { REDUX_AVALANCHE_ADVISORY_FETCH } from 'redux-state/avalanche-advisory/reducer.js';
import { IAvalancheForecast, LoadState } from 'redux-state/avalanche-advisory/types.js';
import { useAppDispatch } from 'redux-state/configure-store.js';
import { IRepoState } from 'redux-state/rootReducer.js';
import { getConfig } from './utils/get-config.js';

interface IProps {
  location: string;
  className?: string;
}

const getLink = (id?: number) => `https://www.avalanche.net.nz${id ? `/region/${id}` : ''}`;

const getLocationData = (data: Array<IAvalancheForecast>, location: string) =>
  data.find(({ mountain }) => mountain === location);

const RenderAvalancheFooter = ({ forecast }: { forecast?: IAvalancheForecast }) => {
  const url = getLink(forecast && forecast.mountain_id);

  return (
    <div className="AvalancheAdvisory-footer">
      {forecast && (
        <span className="AvalancheAdvisory-footer__dates">
          Valid from {forecast.issued_at}
          <br />
          Valid to {forecast.valid_to}
        </span>
      )}
      <LinkFactoryReactRouter
        href={url}
        className="Button Button--AvalancheAdvisory u-print--none"
        target="_blank"
        rel="noopener noreferrer"
      >
        Details <span className="icon icon-chevron-right" />
      </LinkFactoryReactRouter>
    </div>
  );
};

export const AvalancheAdvisory = ({ location, className }: IProps) => {
  const appDispatch = useAppDispatch();
  const { data, loadState } = useSelector((state: IRepoState) => state.AVALANCHE_ADVISORY);

  useEffect(() => {
    appDispatch(REDUX_AVALANCHE_ADVISORY_FETCH());
  }, [appDispatch]);

  // If data exists and loading continue to display widget.
  // Condition added to fix issue with component reloading data
  // when printing
  if (loadState === LoadState.LOADING && !data) {
    return <>Loading</>;
  }

  const forecast = data && getLocationData(data.forecasts, location);

  if (!data || !forecast) {
    return (
      <div className={classNames('AvalancheAdvisory', className)}>
        <div className="AvalancheAdvisory-content AvalancheAdvisory-content--center">
          Avalanche information is currently unavailable.
        </div>
        <RenderAvalancheFooter />
      </div>
    );
  }

  const config = getConfig(data.danger_scales, forecast);
  const url = getLink(forecast.mountain_id);

  return (
    <div className={classNames('AvalancheAdvisory', className)}>
      <div className="AvalancheAdvisory-content">
        <div className="AvalancheAdvisory-conditions">
          <div className="AvalancheAdvisory-conditions__condition">
            <span className="AvalancheAdvisory-conditions__condition__label">{config.high.label}</span>
            <div className={`AvalancheAdvisory-risk ${config.high.className}`}>{config.high.risk}</div>
          </div>
          <div className="AvalancheAdvisory-conditions__condition">
            <span className="AvalancheAdvisory-conditions__condition__label">{config.mid.label}</span>
            <div className={`AvalancheAdvisory-risk ${config.mid.className}`}>{config.mid.risk}</div>
          </div>
          <div className="AvalancheAdvisory-conditions__condition">
            <span className="AvalancheAdvisory-conditions__condition__label">{config.sub.label}</span>
            <div className={`AvalancheAdvisory-risk ${config.sub.className}`}>{config.sub.risk}</div>
          </div>
        </div>
        <div className="AvalancheAdvisory-mountain">
          <LinkFactoryReactRouter href={url} target="_blank" rel="noopener noreferrer">
            <Mountain config={config} />
          </LinkFactoryReactRouter>
        </div>
      </div>
      <RenderAvalancheFooter forecast={forecast} />
    </div>
  );
};
